import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaBoxes, FaChartLine, FaCoins, FaTags, FaTruck, FaWarehouse, FaUsers, FaStore } from "react-icons/fa";
import { previewProviderSales, simulateProviderSales } from "../services/providerProductService";
import { dropshippingText } from "../i18n";
import SimulationTaxInput, { simulationTaxText } from "../../pageInside/SimulationTaxInput";
// Même feuille de style que le Sales Simulation Seller (.sales-sim / .sim-*).
import "../../pageInside/salesSimulation.css";
import "./ProviderSalesSimulation.css";

// Provider Business & Sales Simulation (Provider -> Seller -> Buyer).
// Référence UX/design : pageInside/salesSimulation.jsx (mêmes classes .sim-*).
// Référence métier : le ProviderProduct enregistré. AUCUN calcul financier ici :
// le backend relit le produit (prix, paliers, MOQ, stock, options, taux de
// frais de la catégorie) et renvoie le détail ; ce composant l'affiche. C'est
// une simulation en lecture seule : rien n'est commandé, réservé ni créé.
const money = (value, currency = "USD") => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    return n.toLocaleString("en-US", { style: "currency", currency });
  } catch {
    return n.toFixed(2);
  }
};

const Row = ({ label, value, strong }) => (
  <div className="d-flex justify-content-between provider-sim__row">
    <span className="text-muted">{label}</span>
    <span className={strong ? "font-weight-bold" : ""}>{value}</span>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="sim-stat">
    <span className="sim-stat__label">{label}</span>
    <span className="sim-stat__value">{value}</span>
  </div>
);

const Flag = ({ label, on, t }) => (
  <Row label={label} value={<span className={`badge ${on ? "badge-success" : "badge-secondary"}`}>{on ? t("t038") : t("t039")}</span>} />
);

const MODE_LABEL = {
  SELLER_STOCK: "t109",
  PROVIDER_DROPSHIPPING: "t110",
  PROVIDER_STOCK_SELLER_OWNED: "t111",
  PROVIDER_WHOLESALE_SHIPPING: "t112",
};
const SCENARIO_LABEL = { wholesale: "t077", dropshipping: "t078", volume: "t079" };

// Deux modes :
//  - "create"   : /provider/products/add. Le produit n'existe pas forcément en
//                 base ; `productData` (état du formulaire du wizard) est envoyé
//                 au backend pour validation + calcul. Aucun GET par id.
//  - "existing" : un vrai `productId` est relu via l'API (le "Product not found"
//                 n'apparaît que si le backend confirme PRODUCT_NOT_FOUND).
// `onStatusChange("ok" | "invalid" | "error")` permet au parent de bloquer Next
// uniquement quand les données requises sont invalides.
const FIELD_MESSAGE = { name: "t011", basePrice: "t012", stock: "t028", minimumOrderQuantity: "t121", quantityPricing: "t122" };

const ProviderSalesSimulation = ({ infoSource, token, mode = "existing", productData = null, productId = null, refreshKey = 0, onStatusChange }) => {
  const t = dropshippingText(infoSource, "providerProductWizard");
  const [inputs, setInputs] = useState({ quantity: "", hypotheticalSellerPrice: "", sellerCount: "1", ordersPerSeller: "1", taxRate: 0 });
  const [scenario, setScenario] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);
  const dataKey = JSON.stringify(productData || null);
  const ready = mode === "create" ? Boolean(productData) : Boolean(productId);
  const notify = useRef(onStatusChange);
  notify.current = onStatusChange;

  const params = (values) => {
    const out = {};
    ["quantity", "hypotheticalSellerPrice", "sellerCount", "ordersPerSeller"].forEach((k) => {
      if (values[k] !== "" && Number(values[k]) > 0) out[k] = Number(values[k]);
    });
    if (Number(values.taxRate) > 0) out.taxRate = Number(values.taxRate);
    return out;
  };

  const run = useCallback(
    async (values) => {
      const mine = ++seq.current;
      setLoading(true);
      setError(null);
      const result =
        mode === "create"
          ? await previewProviderSales(token, JSON.parse(dataKey), params(values))
          : await simulateProviderSales(token, productId, params(values));
      if (mine !== seq.current) return;
      setLoading(false);
      if (result?.success === false || result?.error) {
        setData(null);
        setError({ status: result.status, code: result.code, fields: result.fields });
        notify.current?.(result.code === "INVALID_PRODUCT_DATA" ? "invalid" : "error");
        return;
      }
      notify.current?.("ok");
      setData(result);
      setInputs((prev) => (prev.quantity === "" ? { ...prev, quantity: String(result.quantity) } : prev));
      setScenario((prev) => (prev && result.scenarios.includes(prev) ? prev : result.scenarios[0] || null));
    },
    [token, productId, mode, dataKey]
  );

  // Recharge à chaque ouverture de l'étape (refreshKey) : les valeurs
  // modifiées dans les étapes précédentes sont relues depuis le backend.
  useEffect(() => {
    if (!ready) return;
    const fresh = { quantity: "", hypotheticalSellerPrice: "", sellerCount: "1", ordersPerSeller: "1", taxRate: 0 };
    setInputs(fresh);
    run(fresh);
  }, [ready, refreshKey, run, dataKey]);

  if (!ready) return <div className="alert alert-warning">{t(mode === "create" ? "t123" : "t040")}</div>;

  if (error && error.code === "INVALID_PRODUCT_DATA") {
    // Données manquantes/invalides : on dit précisément lesquelles (pas de Retry).
    return (
      <div className="alert alert-warning" role="alert">
        <p className="mb-2">{t("t123")}</p>
        <ul className="mb-0">
          {(error.fields || []).map((f) => (
            <li key={f}>{t(FIELD_MESSAGE[f] || "t123")}</li>
          ))}
        </ul>
      </div>
    );
  }

  if (error) {
    // "Product not found" UNIQUEMENT si le backend le confirme par son code : un
    // 404 nu (route inconnue, serveur pas à jour) est une erreur générique.
    const key = error.code === "FORBIDDEN_PRODUCT" || error.status === 403 ? "t041" : error.code === "PRODUCT_NOT_FOUND" ? "t042" : "t043";
    return (
      <div className="alert alert-danger" role="alert">
        <p className="mb-2">{t(key)}</p>
        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => run(inputs)}>
          {t("t044")}
        </button>
      </div>
    );
  }

  if (!data) return <div className="alert alert-info">{t("t045")}</div>;

  const cur = data.product?.currency || "USD";
  const taxLabel = simulationTaxText(infoSource, "h_sim_tax_amount", "Applicable sales tax");
  const totalLabel = simulationTaxText(infoSource, "h_sim_total_with_tax", "Total including tax");
  const c = (v) => money(v, cur);
  const noPrice = !(Number(data.basePrice) > 0);
  const setInput = (key) => (e) => setInputs((prev) => ({ ...prev, [key]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    run(inputs);
  };
  const numberField = (id, key, label, min = 1, step = "1") => (
    <div className="provider-sim__field">
      <label className="text-muted" htmlFor={id}>{label}</label>
      <input id={id} type="number" min={min} step={step} className="form-control" value={inputs[key]} onChange={setInput(key)} />
    </div>
  );
  const applyButton = (
    <button type="submit" className="btn btn-primary provider-sim__apply" disabled={loading || !(Number(inputs.quantity) >= 1)}>
      {t("t053")}
    </button>
  );
  const ds = data.dropshipping?.simulated;
  const st = data.stockStrategy;
  const vol = data.volume;

  return (
    <div className="sales-sim provider-sim" aria-busy={loading}>
      <div className="sim-card">
        <h3 className="sim-card__title">
          <FaBoxes className="sim-card__icon" /> {data.product?.name || t("t046")}
        </h3>
        <div className="d-flex align-items-start">
          {data.product?.images?.[0] && <img className="provider-sim__img mr-3" src={data.product.images[0]} alt="" />}
          <div className="w-100">
            <Row label={t("t002")} value={c(data.basePrice)} strong />
            <Row label={t("t020")} value={data.minimumOrderQuantity} />
            <Row label={t("t004")} value={data.stock} />
            <Row label={t("t047")} value={t(data.product?.status === "active" ? "t049" : "t048")} />
          </div>
        </div>
        {noPrice && <div className="alert alert-warning mt-2 mb-0">{t("t050")}</div>}
      </div>

      {data.scenarios.length === 0 ? (
        <div className="alert alert-info">{t("t080")}</div>
      ) : (
        <div className="sim-card">
          <div className="provider-sim__tabs" role="tablist" aria-label={t("t076")}>
            {data.scenarios.map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={scenario === key}
                className={`btn ${scenario === key ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setScenario(key)}
              >
                {t(SCENARIO_LABEL[key])}
              </button>
            ))}
          </div>
          <small className="form-text text-muted mb-3">{t("t120")}</small>
          <SimulationTaxInput infoSource={infoSource} value={inputs.taxRate}
            onChange={rate => {
              const next = { ...inputs, taxRate: rate };
              setInputs(next);
              run(next);
            }} />

          {scenario === "wholesale" && (
            <>
              <h3 className="sim-card__title">
                <FaChartLine className="sim-card__icon" /> {t("t051")}
              </h3>
              <form className="provider-sim__form" onSubmit={submit}>
                {numberField("ps-qty", "quantity", t("t052"))}
                {applyButton}
              </form>
              <div className={`alert ${data.wholesaleEligible ? "alert-success" : "alert-warning"}`}>
                {data.wholesaleEligible ? t("t081") : t("t082")}
                {!data.meetsMinimumOrderQuantity && <> — {t("t054", { min: data.minimumOrderQuantity })}</>}
              </div>
              {!data.stockSufficient && <div className="alert alert-warning">{t("t055", { stock: data.stock })}</div>}
              <div className="sim-stat-grid">
                <Stat label={t("t056")} value={c(data.basePrice)} />
                <Stat label={t("t057")} value={`${data.discountPercent}%`} />
                <Stat label={t("t058")} value={c(data.effectiveUnitPrice)} />
                <Stat label={t("t059")} value={c(data.grossTotal)} />
                <Stat label={taxLabel} value={c(data.taxAmount ?? 0)} />
                <Stat label={totalLabel} value={c(data.totalWithTax ?? data.grossTotal)} />
              </div>
              <div className="sim-result-grid mt-3">
                <div className="sim-result sim-result--profit">
                  <span className="sim-result__label"><FaCoins /> {t("t060")}</span>
                  <span className="sim-result__value">{c(data.estimatedProviderRevenue)}</span>
                </div>
                <div className="sim-result sim-result--statut">
                  <span className="sim-result__label">{t("t061")}</span>
                  <span className="sim-statut-badge">{data.appliedTier ? `≥ ${data.appliedTier.minQuantity}` : t("t062")}</span>
                </div>
              </div>
              <Row label={t("t088")} value={t("t091")} />
              <small className="form-text text-muted">{t("t063")}</small>
            </>
          )}

          {scenario === "dropshipping" && data.dropshipping && (
            <>
              <h3 className="sim-card__title">
                <FaStore className="sim-card__icon" /> {t("t078")}
              </h3>
              <Flag label={t("t070")} on={data.dropshipping.paid} t={t} />
              <Row label={t("t085")} value={c(data.dropshipping.providerRevenuePerUnit)} strong />
              <small className="form-text text-muted mb-3">{t("t092")}</small>
              <form className="provider-sim__form" onSubmit={submit}>
                {numberField("ps-hypo", "hypotheticalSellerPrice", t("t084"), "0.01", "0.01")}
                {applyButton}
              </form>
              <div className="alert alert-secondary">{t("t083")}</div>
              {ds ? (
                <>
                  {ds.sellsAtALoss && <div className="alert alert-warning">{t("t118")}</div>}
                  <div className="sim-stat-grid">
                    <Stat label={t("t084")} value={c(ds.hypotheticalSellerPrice)} />
                    <Stat label={t("t085")} value={c(ds.providerRevenue)} />
                    <Stat label={t("t086")} value={`${c(ds.sellerGrossMargin)} (${ds.sellerGrossMarginPercent}%)`} />
                    <Stat label={t("t088")} value={c(ds.elakayFees)} />
                    <Stat label={t("t089")} value={c(ds.paymentFees)} />
                    <Stat label={taxLabel} value={c(ds.taxAmount ?? 0)} />
                    <Stat label={totalLabel} value={c(ds.customerTotal ?? ds.hypotheticalSellerPrice)} />
                    <Stat label={t("t090")} value={t("t091")} />
                  </div>
                  <div className="sim-result-grid mt-3">
                    <div className={`sim-result ${ds.sellsAtALoss ? "sim-result--loss" : "sim-result--profit"}`}>
                      <span className="sim-result__label">{t("t087")}</span>
                      <span className="sim-result__value">{`${c(ds.sellerNetMargin)} (${ds.sellerNetMarginPercent}%)`}</span>
                    </div>
                  </div>
                  <small className="form-text text-muted">{t("t093", { percent: ds.categoryFeePercent })}</small>
                </>
              ) : null}
            </>
          )}

          {scenario === "volume" && vol && (
            <>
              <h3 className="sim-card__title">
                <FaUsers className="sim-card__icon" /> {t("t079")}
              </h3>
              <form className="provider-sim__form" onSubmit={submit}>
                {numberField("ps-vq", "quantity", t("t052"))}
                {numberField("ps-sellers", "sellerCount", t("t094"))}
                {numberField("ps-orders", "ordersPerSeller", t("t095"))}
                {applyButton}
              </form>
              <div className="sim-stat-grid">
                <Stat label={t("t096")} value={vol.totalOrders} />
                <Stat label={t("t097")} value={vol.projectedUnits} />
                <Stat label={t("t100")} value={c(vol.revenuePerOrder)} />
                <Stat label={t("t099")} value={c(vol.revenuePerSeller)} />
                <Stat label={taxLabel} value={c(vol.taxAmount ?? 0)} />
                <Stat label={totalLabel} value={c(vol.totalWithTax ?? vol.projectedGrossRevenue)} />
              </div>
              <div className="sim-result-grid mt-3">
                <div className="sim-result sim-result--profit">
                  <span className="sim-result__label"><FaCoins /> {t("t098")}</span>
                  <span className="sim-result__value">{c(vol.projectedGrossRevenue)}</span>
                </div>
              </div>
              <div className={`alert mt-3 ${vol.stockCoversProjection ? "alert-success" : "alert-warning"}`}>
                {vol.stockCoversProjection ? t("t101") : t("t102", { units: vol.unitsShort })}
              </div>
              <small className="form-text text-muted">{t("t103")}</small>
            </>
          )}
        </div>
      )}

      <div className="sim-card">
        <h3 className="sim-card__title">
          <FaTags className="sim-card__icon" /> {t("t064")}
        </h3>
        {data.tiers.length === 0 ? (
          <p className="text-muted mb-0">{t("t065")}</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm provider-sim__tiers">
              <thead>
                <tr>
                  <th>{t("t052")}</th>
                  <th>{t("t058")}</th>
                  <th>{t("t057")}</th>
                  <th>{t("t066")}</th>
                  <th>{t("t060")}</th>
                </tr>
              </thead>
              <tbody>
                {data.tiers.map((tier) => (
                  <tr
                    key={tier.minQuantity}
                    className={data.appliedTier?.minQuantity === tier.minQuantity ? "table-active" : ""}
                    onClick={() => {
                      const next = { ...inputs, quantity: String(tier.minQuantity) };
                      setInputs(next);
                      run(next);
                    }}
                  >
                    <td data-label={t("t052")}>{tier.minQuantity}{tier.maxQuantity ? `–${tier.maxQuantity}` : "+"}</td>
                    <td data-label={t("t058")}>{c(tier.unitPrice)}</td>
                    <td data-label={t("t057")}>{tier.discountPercent}%</td>
                    <td data-label={t("t066")}>{c(tier.orderTotal)}</td>
                    <td data-label={t("t060")}>{c(tier.providerRevenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="sim-card">
        <h3 className="sim-card__title">
          <FaTruck className="sim-card__icon" /> {t("t104")}
        </h3>
        {data.fulfillment.modes.length === 0 ? (
          <p className="text-muted">{t("t119")}</p>
        ) : (
          data.fulfillment.modes.map((m) => (
            <div key={m.mode} className="provider-sim__mode">
              <strong>{t(MODE_LABEL[m.mode])}</strong>
              <Row label={t("t105")} value={t(m.stores === "provider" ? "t107" : "t108")} />
              <Row label={t("t106")} value={t(m.ships === "provider" ? "t107" : "t108")} />
              <Row label={t("t090")} value={m.estimatedCost === null ? t("t091") : c(m.estimatedCost)} />
            </div>
          ))
        )}
        <Flag label={t("t021")} on={data.wholesale.wholesaleEnabled} t={t} />
        <Flag label={t("t068")} on={data.wholesale.providerStorageEnabled} t={t} />
        <Flag label={t("t072")} on={data.fulfillment.sellerPickupEnabled} t={t} />
        <Flag label={t("t073")} on={data.fulfillment.localDeliveryEnabled} t={t} />
        <Row label={t("t009")} value={data.fulfillment.allowedCountries.length ? data.fulfillment.allowedCountries.join(", ") : t("t074")} />
      </div>

      <div className="sim-card">
        <h3 className="sim-card__title">
          <FaWarehouse className="sim-card__icon" /> {t("t113")}
        </h3>
        <Row label={t("t004")} value={st.sourceStock} />
        <Row label={t("t116")} value={st.storedForSellers} />
        <Row label={t("t020")} value={st.minimumOrderQuantity} />
        <Row label={t("t114")} value={st.maxMinimumOrders} />
        <Row label={t("t115")} value={st.maxOrdersAtQuantity} />
        <small className="form-text text-muted">{t("t103")}</small>
      </div>
    </div>
  );
};

export default ProviderSalesSimulation;
